import {
  Avatar,
  AvatarGroup,
  Button,
  HStack,
  Icon,
  Image,
  Stack,
  StackProps,
  Text,
  VStack,
} from "@chakra-ui/react";
import { RiCalendar2Line } from "@remixicon/react";
import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  OrderStatusInterface,
  StatusInterface,
} from "../../constants/OrderStatus";
import { useDarkWhiteColor } from "../../themes/colors";
import {
  formatDate,
  formatTime,
  isDueDatePassed,
  statusOrderNote,
} from "../../utils/helperFunction";
import { ConfirmOrderButton } from "../dashboard_buyer_page/ConfirmOrderButton";
import { AddResiModal } from "../dashboard_supplier/AddResiModal";

interface Props extends StackProps {
  unpaid?: boolean;
  item: OrderStatusInterface;
  pageType: number; // 1=buyer, 2=reseller, 3=supplier
}

export const OrderStatusCard = ({
  unpaid = false,
  item,
  pageType,
  ...rest
}: Props) => {
  const [onRefresh, setOnRefresh] = useState<number>(0);
  const [status, setStatus] = useState<StatusInterface>();
  const bg = useDarkWhiteColor();
  const navigate = useNavigate();

  useEffect(() => {
    setStatus(statusOrderNote(item.status, unpaid));
  }, [item.status, unpaid]);

  const handleDetail = () => {
    if (unpaid) {
      navigate(`/transaction?type=payment&paymentId=${item.id}`);
    } else {
      navigate(
        `/${
          pageType === 1 ? "buyer" : pageType === 2 ? "reseller" : "supplier"
        }/order/${item.id}`
      );
    }
  };

  const ButtonAction = () => {
    if (unpaid) {
      if (unpaid && item.status !== 0) {
        return undefined;
      }

      return (
        <Button
          variant={!unpaid ? "ghost" : "outline"}
          colorScheme="red"
          size={["xs", null, "sm"]}
          flex={[1, 1, 1, "none", "none"]}
          onClick={handleDetail}
          isDisabled={
            unpaid && item.due_date && isDueDatePassed(item.due_date.toString())
              ? true
              : false
          }
        >
          {!unpaid
            ? `Lihat Detail`
            : unpaid &&
              item.due_date &&
              isDueDatePassed(item.due_date?.toString())
            ? "Pembayaran Terlambat"
            : "Selesaikan Pembayaran"}
        </Button>
      );
    }

    return (
      <Button
        variant={!unpaid ? "ghost" : "outline"}
        colorScheme="red"
        size={["md", null, "sm"]}
        flex={[1, 1, 1, "none", "none"]}
        onClick={handleDetail}
      >
        Lihat Detail
      </Button>
    );
  };

  const TextDueDate = () => {
    if (unpaid && item.due_date && item.status === 0) {
      return (
        <Text as={"i"} color={"red.400"} textStyle={"b-sm"} mt={2}>
          {`Selesaikan pembayaran sebelum ${formatDate(
            item.due_date?.toString()
          )} Jam ${formatTime(item.due_date.toString())}`}
        </Text>
      );
    }
  };

  return (
    <VStack
      w={"100%"}
      h={"100%"}
      maxH={"300px"}
      p={4}
      bg={bg}
      borderRadius={["xl", null, "md"]}
      align={"stretch"}
      {...rest}
    >
      <HStack>
        <Icon as={RiCalendar2Line} opacity={0.6} fontSize={[14, null, 12]} />
        <Text fontSize={[12, null, 10]} variant={"secondary"}>
          {item.purchasedDate && formatDate(item.purchasedDate.toString())}
        </Text>
      </HStack>

      <Stack
        direction={["column", "column", "column", "row", "row"]}
        w={"100%"}
        gap={2}
        alignItems={"start"}
        justify={"space-between"}
      >
        <HStack flex={[2, null, 1]} w={"100%"} alignItems={"start"}>
          <Image
            className="ratio-1-1"
            src={
              item.images[0]
                ? `${process.env.REACT_APP_MEDIA_URL}${item.images[0]}`
                : ""
            }
            maxW={["60px", null, "75px"]}
            objectFit={"cover"}
            alt="Image Transaction"
            fallbackSrc="/assets/svg/illustration_empty.svg"
            borderRadius={"xl"}
          />

          <VStack align={"stretch"} justify={"space-between"} gap={0}>
            {/* <Text textStyle={"b-sm-sb"} noOfLines={1} textOverflow={"ellipsis"}> */}
            <Text
              fontSize={[16, null, 12]}
              fontWeight={"semibold"}
              noOfLines={1}
              textOverflow={"ellipsis"}
            >
              {item.productName}
            </Text>

            {item.images.length > 1 && (
              <HStack mt={3}>
                <AvatarGroup max={2} size={"xs"}>
                  {item.images.slice(1).map((image, i) => (
                    <Avatar
                      key={i}
                      name={item.productName || ""}
                      src={
                        image
                          ? `${process.env.REACT_APP_MEDIA_URL}${image}`
                          : ""
                      }
                    />
                  ))}
                </AvatarGroup>

                <Text fontSize={[12, null, 10]} variant={"secondary"}>
                  {item.images.length - 1} Produk lainnya...
                </Text>
              </HStack>
            )}
          </VStack>
        </HStack>

        <Stack
          className="root-button-status-order"
          w={["100%", "100%", "100%", "fit-content", "fit-content"]}
          h={"100%"}
          mt={[4, 4, 4, 0, 0]}
          gap={[4, 4, 4, 0, 0]}
          align={["stretch", "stretch", "stretch", "end", "end"]}
          justify={"space-between"}
          direction={
            pageType === 1 || pageType === 3
              ? "column"
              : ["row", "row", "row", "column", "column"]
          }
        >
          <HStack className="status-order" color={status?.color} mr={3}>
            <Icon as={status?.icon} />
            {/* <Text textStyle={"b-reg-sb"}>{item.statusName}</Text> */}
            <Text fontSize={14} fontWeight={600}>
              {item.statusName}
            </Text>
          </HStack>

          <HStack
            className="wrap-button"
            w={
              pageType === 1 || pageType === 3
                ? ["100%", "100%", "100%", "fit-content", "fit-content"]
                : "fit-content"
            }
            gap={4}
            mt={[0, 0, 0, 6, 6]}
          >
            <ButtonAction />

            {pageType === 1 && !unpaid && item.status === 4 && (
              <ConfirmOrderButton
                orderId={item.id}
                onRefresh={(type: number) => setOnRefresh(type)}
              />
            )}

            {pageType === 3 &&
              item.status === 1 &&
              !item.resi &&
              onRefresh === 0 && (
                <AddResiModal
                  id={item.id}
                  onRefresh={(type: number) => setOnRefresh(type)}
                />
              )}
          </HStack>

          <TextDueDate />
        </Stack>
      </Stack>
    </VStack>
  );
};
